import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Megaphone, ListChecks, AlertTriangle, CheckCircle2, ClipboardList, FolderKanban } from 'lucide-react';
import { useUser } from '@/shared/hooks/useAuth';
import { ROUTES } from '@/routes/paths';
import { cn } from '@/shared/utils/utils';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { DashboardHero } from '../infrastructure/widgets/DashboardHero';
import { useMarketingProjects } from '../widgets/marketing/hooks/useMarketingProjects';
import { useMarketingTasks, TaskItem } from '../widgets/marketing/hooks/useMarketingTasks';

const STATUS_DOT: Record<string, string> = {
  todo: 'bg-on-surface-subtle',
  in_progress: 'bg-info',
  review: 'bg-warning',
  done: 'bg-success',
  completed: 'bg-success',
};

const PRIORITY_BADGE: Record<string, string> = {
  critical: 'bg-danger/10 text-danger-text',
  high: 'bg-warning/10 text-warning-text',
  medium: 'bg-primary/10 text-primary',
  low: 'bg-surface-variant text-on-surface-variant',
};

const PROJECT_STATUS_BADGE: Record<string, string> = {
  active: 'bg-success/10 text-success-text',
  on_hold: 'bg-warning/10 text-warning-text',
  completed: 'bg-info/10 text-info-text',
  cancelled: 'bg-danger/10 text-danger-text',
};

function taskAssignee(t: TaskItem): string {
  return t.assignee_name || t.assigned_name || '';
}

function fmtDate(d?: string) {
  if (!d) return '';
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function SectionHeader({
  icon: Icon,
  title,
  subtitle,
  actionTo,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  subtitle: string;
  actionTo?: string;
}) {
  return (
    <div className="mb-3 flex items-center justify-between gap-3 border-b border-border pb-3">
      <div className="flex min-w-0 items-center gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-surface-container text-on-surface-variant">
          <Icon className="h-[18px] w-[18px]" />
        </div>
        <div className="min-w-0">
          <h3 className="text-body-sm font-semibold text-on-surface">{title}</h3>
          <p className="truncate text-body-sm text-on-surface-variant">{subtitle}</p>
        </div>
      </div>
      {actionTo && (
        <Link to={actionTo} className="shrink-0 text-body-sm font-semibold text-primary hover:underline">
          View all →
        </Link>
      )}
    </div>
  );
}

export function MarketingEmployeeDashboard() {
  const user = useUser();
  // Both hooks are server-scoped to the logged-in user for the 'employee'
  // role: GET /api/tasks filters to { assigned_to: uid OR created_by: uid },
  // GET /api/projects filters the same way (see projectScopeFilter in
  // src/security/record-authorization.js). So "all" here already means "mine".
  const { data: projects, isLoading: projectsLoading } = useMarketingProjects();
  const { data: tasks, isLoading: tasksLoading } = useMarketingTasks();

  const isLoading = projectsLoading || tasksLoading;

  const stats = useMemo(() => {
    const now = Date.now();
    const myProjects = projects ?? [];
    const myTasks = tasks ?? [];

    const totalProjects = myProjects.length;
    const activeProjects = myProjects.filter((p) => p.status === 'active').length;

    const totalTasks = myTasks.length;
    const openTasks = myTasks.filter((t) => t.status !== 'done' && t.status !== 'completed').length;
    const completedTasks = myTasks.filter((t) => t.status === 'done' || t.status === 'completed').length;
    const overdueTasks = myTasks.filter(
      (t) => t.due_date && new Date(t.due_date).getTime() < now && t.status !== 'done' && t.status !== 'completed',
    ).length;

    const openTasksSorted = myTasks
      .filter((t) => t.status !== 'done' && t.status !== 'completed')
      .sort((a, b) => {
        const order: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3 };
        const pa = order[a.priority] ?? 4;
        const pb = order[b.priority] ?? 4;
        if (pa !== pb) return pa - pb;
        if (a.due_date && b.due_date) return a.due_date.localeCompare(b.due_date);
        if (a.due_date) return -1;
        if (b.due_date) return 1;
        return 0;
      });

    return { totalProjects, activeProjects, totalTasks, openTasks, completedTasks, overdueTasks, openTasksSorted, myProjects };
  }, [projects, tasks]);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-6">
        <Skeleton className="h-[180px] rounded-[22px]" />
        <Skeleton className="h-[360px] rounded-card" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <DashboardHero
        badge="Digital Marketing"
        badgeIcon={Megaphone}
        title={`Welcome back, ${user?.displayName || 'Team Member'}`}
        subtitle="Here are your marketing tasks and project deadlines."
        heroStats={[
          // One row of figures — a card row underneath used to repeat them.
          { label: 'Open tasks', value: String(stats.openTasks), sub: `${stats.totalTasks} total`, icon: ListChecks, tone: 'info' },
          { label: 'Overdue tasks', value: String(stats.overdueTasks), sub: stats.overdueTasks > 0 ? 'Needs attention' : 'All on track', icon: AlertTriangle, tone: stats.overdueTasks > 0 ? 'danger' : 'success' },
          { label: 'Completed', value: String(stats.completedTasks), sub: 'tasks done', icon: CheckCircle2, tone: 'success' },
          { label: 'My projects', value: String(stats.totalProjects), sub: `${stats.activeProjects} active`, icon: Megaphone, tone: 'primary' },
        ]}
      />

      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12 lg:col-span-7 rounded-card border border-border bg-card p-4 shadow-xs">
          <SectionHeader icon={ClipboardList} title="My Tasks" subtitle="Your open marketing tasks" actionTo={ROUTES.tasks} />
          {stats.openTasksSorted.length === 0 ? (
            <div className="py-10 text-center text-body-sm text-on-surface-variant">No open tasks assigned to you.</div>
          ) : (
            <div className="flex flex-col gap-2">
              {stats.openTasksSorted.slice(0, 6).map((t) => {
                const assignee = taskAssignee(t);
                return (
                  <div
                    key={t._id}
                    className="flex items-center justify-between gap-3 rounded-card-sm border border-border bg-surface-variant px-3.5 py-2.5"
                  >
                    <div className="flex min-w-0 items-center gap-3">
                      <span className={cn('h-2 w-2 shrink-0 rounded-full', STATUS_DOT[t.status] || 'bg-on-surface-subtle')} />
                      <div className="min-w-0">
                        <p className="truncate text-data-md font-semibold text-on-surface">{t.title}</p>
                        <p className="truncate text-body-sm text-on-surface-subtle">
                          {t.project_name || 'General Task'}
                          {assignee && ` · ${assignee}`}
                          {t.due_date && ` · Due ${fmtDate(t.due_date)}`}
                        </p>
                      </div>
                    </div>
                    <span
                      className={cn(
                        'shrink-0 rounded-full px-2 py-0.5 text-data-xs font-semibold capitalize',
                        PRIORITY_BADGE[t.priority] || PRIORITY_BADGE.low,
                      )}
                    >
                      {t.priority}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="col-span-12 lg:col-span-5 rounded-card border border-border bg-card p-4 shadow-xs">
          <SectionHeader icon={FolderKanban} title="My Projects" subtitle="Campaigns you're part of" actionTo={ROUTES.projects} />
          {stats.myProjects.length === 0 ? (
            <div className="py-10 text-center text-body-sm text-on-surface-variant">You're not assigned to any projects yet.</div>
          ) : (
            <div className="flex flex-col gap-2">
              {stats.myProjects.slice(0, 6).map((p) => (
                <div
                  key={p._id}
                  className="flex items-center justify-between gap-3 rounded-card-sm border border-border bg-surface-variant px-3.5 py-2.5"
                >
                  <div className="min-w-0">
                    <p className="truncate text-data-md font-semibold text-on-surface">{p.name}</p>
                    {p.deadline && <p className="truncate text-body-sm text-on-surface-subtle">Deadline {fmtDate(p.deadline)}</p>}
                  </div>
                  <span
                    className={cn(
                      'shrink-0 rounded-full px-2 py-0.5 text-data-xs font-semibold capitalize',
                      PROJECT_STATUS_BADGE[p.status] || PROJECT_STATUS_BADGE.active,
                    )}
                  >
                    {p.status.replace('_', ' ')}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
